import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Button,
  Column,
  Columns,
  Heading,
  Loader,
  PageBlock,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import { useSearchParams } from 'react-router-dom';

import { Nav } from 'src/components/Nav/Nav';
import { PageWrapper } from 'src/components/PageWrapper/PageWrapper';
import translations from 'src/pages/ConnectedPartners/.vocab';
import lysTranslations from 'src/pages/LinkYourSoftware/.vocab';
import type { PageProps } from 'src/types';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';
import { useQueryHistory } from 'src/utils/hooks/useQueryHistory';
import { chooseConnectedPartners } from 'src/utils/partners';
import { slugify } from 'src/utils/slugify';

import { BadgeGuide } from './components/BadgeGuide';
import { ContactPanel } from './components/ContactPanel';
import { FooterBanner } from './components/FooterBanner';
import { HeaderBanner } from './components/HeaderBanner';
import { PartnerList } from './components/PartnerList/PartnerList';
import { RequestPanel } from './components/RequestPanel';
import { PartnerFilter } from './components/SearchFilter/PartnerFilter';
import { RegionFilter } from './components/SearchFilter/RegionFilter';
import {
  groupPartners,
  makePartnerSuggestions,
} from './components/SearchFilter/makePartnerSuggestions';
import { SolutionsFilter } from './components/SolutionsFilter/SolutionsFilter';
import type { Solution } from './components/SolutionsFilter/solutionsData';

export const ConnectedPartners = ({ slug }: PageProps) => {
  const { t: lys } = useTranslations(lysTranslations);
  const { t } = useTranslations(translations);
  const pageTrackingProps = {
    page: `${slugify({ str: slug })}`,
    eventName: `${slugify({ str: slug })}-viewed`,
    siteSection: 'partners',
  };

  const connectedPartners = chooseConnectedPartners(
    useIntegrationPartners().partners,
  );

  const [, setSearch] = useSearchParams();

  const { value: partnerFilter } = useQueryHistory('name');
  const { value: regionFilter } = useQueryHistory('region');
  const { values: solutionFilter } = useQueryHistory('solutions');

  const clearAllFilters = async () => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('region', 'All');

    const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.history.pushState({}, '', newUrl);

    setSearch(newSearchParams);
  };

  const showRegionAlert =
    connectedPartners.state === 'data' && regionFilter === 'All';

  return (
    <PageWrapper
      pageTrackingProps={pageTrackingProps}
      title={slug}
      footerPosition="belowFold"
    >
      <Nav activeTab="connected-partners" />
      <PageBlock width="large">
        <HeaderBanner />
        <Box
          width="full"
          paddingTop="medium"
          paddingBottom="xxlarge"
          paddingX={['xxsmall', 'gutter', 'gutter']}
        >
          <Columns space="large" collapseBelow="tablet">
            <Column width="1/3">
              <Stack space="large">
                <Box>
                  <Stack space="medium">
                    <Heading level="4">{t('filters')}</Heading>
                    <RegionFilter disabled={Boolean(partnerFilter)} />
                    <PartnerFilter
                      disabled={false}
                      groupedSuggestions={makePartnerSuggestions(
                        connectedPartners.state === 'data'
                          ? connectedPartners.data
                          : [],
                        regionFilter ?? undefined,
                      )}
                    />
                    <SolutionsFilter disabled={Boolean(partnerFilter)} />
                    <Button
                      onClick={clearAllFilters}
                      variant="transparent"
                      tone="formAccent"
                    >
                      {t('clearAll')}
                    </Button>
                  </Stack>
                </Box>
                <BadgeGuide />
              </Stack>
            </Column>
            <Column>
              {showRegionAlert ? (
                <Box paddingBottom="gutter">
                  <Alert tone="info">
                    <Text size="small">{t('pleaseSelectLocation')}</Text>
                  </Alert>
                </Box>
              ) : null}
              {connectedPartners.state === 'data' ? (
                <>
                  {connectedPartners.data.length > 0 ? (
                    <PartnerList
                      data={groupPartners(connectedPartners.data)}
                      partnerFilter={partnerFilter ?? ''}
                      regionFilter={regionFilter ?? ''}
                      solutionFilter={solutionFilter as Array<Solution['slug']>}
                    />
                  ) : null}
                  <FooterBanner />
                  <Tiles space="small" columns={[1, 1, 2]}>
                    <RequestPanel />
                    <ContactPanel />
                  </Tiles>
                </>
              ) : (
                <>
                  {connectedPartners.state === 'loading' ? (
                    <Loader delayVisibility />
                  ) : (
                    <Alert tone="critical">
                      <Text>{lys('somethingWentWrong')}</Text>
                    </Alert>
                  )}
                </>
              )}
            </Column>
          </Columns>
        </Box>
      </PageBlock>
    </PageWrapper>
  );
};
